import React, { useEffect, useRef } from "react";
import AIAutomationChat from "../components/AIAutomationChat";

const AIAutomation = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const nodes = Array.from({ length: 40 }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      r: Math.random() * 2 + 1,
      dx: (Math.random() - 0.5) * 0.5,
      dy: (Math.random() - 0.5) * 0.5,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "rgba(100, 180, 255, 0.8)";

      for (let node of nodes) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        ctx.fill();
      }

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.strokeStyle = `rgba(150, 100, 255, ${1 - dist / 120})`;
            ctx.lineWidth = 0.3;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      for (let node of nodes) {
        node.x += node.dx;
        node.y += node.dy;
        if (node.x < 0 || node.x > width) node.dx *= -1;
        if (node.y < 0 || node.y > height) node.dy *= -1;
      }

      requestAnimationFrame(draw);
    };

    draw();
    
    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    
    window.addEventListener("resize", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="relative bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white min-h-screen overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* Hero Section */}
      <section className="flex flex-col items-center justify-center text-center py-32 px-6 relative z-10">
        <div className="animate-fade-in">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text">
            Automate. Innovate. Elevate.
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8 text-lg">
            We build intelligent automation systems using <span className="text-blue-400 font-semibold">n8n</span>, <span className="text-purple-400 font-semibold">AI</span>, and <span className="text-pink-400 font-semibold">Machine Learning</span> — empowering your business to scale smarter.
          </p>
          <p/>
          <div className="flex gap-4 justify-center">
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
              Book a Demo
            </button>
          </div>
        </div>

        {/* Background Glow Animation */}
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
      </section>

      {/* Services Section */}
      <section className="py-20 px-8 md:px-16 bg-gradient-to-b from-gray-950 to-gray-900 relative z-10">
        <h2 className="text-4xl font-semibold text-center mb-4">What We Do</h2>
        <h3 className="text-2xl font-semibold text-center mb-4 text-blue-400">Custom AI For Your Legacy Systems</h3>
        <p className="text-gray-300 text-center max-w-3xl mx-auto mb-12 text-lg">
          We don't just deploy AI. We embed it seamlessly into your existing systems and processes—so your business works smarter, not differently.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: "AI Automation using n8n",
              desc: "Visual, low-code automation with AI integration — build intelligent workflows that save hours daily.",
              icon: (
                <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              ),
            },
            {
              title: "AI & ML Consultancy",
              desc: "From data to deployment — model selection, fine-tuning, and building RAG-based intelligence systems.",
              icon: (
                <svg className="w-10 h-10 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              ),
            },
            {
              title: "Data Engineering",
              desc: "Build scalable pipelines, integrate databases, and set up vector search systems with PGVector & more.",
              icon: (
                <svg className="w-10 h-10 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              ),
            },
          ].map((service, i) => (
            <div
              key={i}
              className="bg-gray-900 border border-gray-800 hover:border-blue-500 transition-all duration-300 hover:-translate-y-1 rounded-lg p-8 flex flex-col items-center text-center"
            >
              {service.icon}
              <h3 className="text-2xl font-semibold mt-4 mb-2">{service.title}</h3>
              <p className="text-gray-400">{service.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-gray-950 px-8 md:px-20 relative z-10">
        <h2 className="text-4xl font-semibold text-center mb-16">How It Works</h2>
        <div className="grid md:grid-cols-4 gap-10 text-center">
          {[
            { step: "01", title: "Discovery", desc: "We analyze your business processes and identify automation opportunities." },
            { step: "02", title: "Design", desc: "Our team designs a visual workflow using n8n & AI logic nodes." },
            { step: "03", title: "Implementation", desc: "We integrate APIs, deploy models, and automate your tasks end-to-end." },
            { step: "04", title: "Optimization", desc: "Monitor, analyze, and continuously improve system performance." },
          ].map((item, i) => (
            <div
              key={i}
              className="p-8 bg-gray-900 rounded-2xl border border-gray-800 hover:border-purple-500 transition hover:scale-105"
            >
              <h3 className="text-5xl font-bold text-purple-500 mb-4">{item.step}</h3>
              <h4 className="text-2xl mb-2">{item.title}</h4>
              <p className="text-gray-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="text-center py-24 bg-gradient-to-r from-blue-900 via-purple-900 to-pink-900 relative z-10">
        <svg className="w-12 h-12 mx-auto text-pink-400 mb-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <h2 className="text-4xl font-bold mb-4">Ready to Automate Your Business?</h2>
        <p className="text-gray-200 mb-8">Let's design intelligent systems that work for you — 24/7.</p>
        <button className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
          Schedule a Free Consultation
        </button>
      </section>

      {/* Chat Component */}
      <AIAutomationChat />
    </div>
  );
};

export default AIAutomation;
